import { Clock, Info } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataSourceConfig, SyncSchedule } from "@/types/dataSource";

const DAYS_OF_WEEK = [
  { id: "mon", label: "Mon", fullName: "Monday" },
  { id: "tue", label: "Tue", fullName: "Tuesday" },
  { id: "wed", label: "Wed", fullName: "Wednesday" },
  { id: "thu", label: "Thu", fullName: "Thursday" },
  { id: "fri", label: "Fri", fullName: "Friday" },
  { id: "sat", label: "Sat", fullName: "Saturday" },
  { id: "sun", label: "Sun", fullName: "Sunday" },
];

// Indexed by JavaScript Date.getDay() (0=Sunday .. 6=Saturday)
const DAY_IDS_BY_JS_DAY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const SYNC_FREQUENCIES = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
  { value: "240", label: "4 hours" },
  { value: "360", label: "6 hours" },
  { value: "720", label: "12 hours" },
  { value: "1440", label: "24 hours (Daily)" },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, "0");
  return { value: `${hour}:00`, label: `${hour}:00` };
});

interface SyncScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataSource: DataSourceConfig;
  onSave: (schedule: SyncSchedule) => void;
}

export const SyncScheduleModal: React.FC<SyncScheduleModalProps> = ({
  open,
  onOpenChange,
  dataSource,
  onSave,
}) => {
  const [selectedDays, setSelectedDays] = useState<string[]>([
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
  ]);
  const [startTime, setStartTime] = useState("06:00");
  const [endTime, setEndTime] = useState("18:00");
  const [syncFrequency, setSyncFrequency] = useState("1440"); // in minutes — default to Daily
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // Initialize with existing schedule when modal opens
  useEffect(() => {
    if (open && dataSource.syncSchedule) {
      setSelectedDays(dataSource.syncSchedule.daysOfWeek);
      setStartTime(dataSource.syncSchedule.startTime);
      setEndTime(dataSource.syncSchedule.endTime);
      const freq = dataSource.syncSchedule.frequencyMinutes;
      const validFreq =
        freq !== 0 && SYNC_FREQUENCIES.some((f) => f.value === freq.toString())
          ? freq.toString()
          : "1440";
      setSyncFrequency(validFreq);
    }
  }, [open, dataSource]);

  const toggleDay = (dayId: string) => {
    setSelectedDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId],
    );
  };

  const calculateSyncsPerDay = () => {
    const start = parseInt(startTime.split(":")[0]);
    const end = parseInt(endTime.split(":")[0]);
    const hours = end - start;
    const frequency = parseInt(syncFrequency) / 60; // convert to hours
    if (frequency === 0 || hours <= 0) return 0;
    return Math.floor(hours / frequency);
  };

  const getNextSyncTime = () => {
    if (selectedDays.length === 0) {
      return "No days selected";
    }

    const now = new Date();
    const currentHour = now.getHours();
    const startHour = parseInt(startTime.split(":")[0]);
    const endHour = parseInt(endTime.split(":")[0]);

    const todayDayId = DAY_IDS_BY_JS_DAY[now.getDay()];

    if (selectedDays.includes(todayDayId) && currentHour < endHour) {
      if (currentHour < startHour) {
        return `Today at ${startTime}`;
      }
      const frequencyHours = parseInt(syncFrequency) / 60;
      const nextHour = Math.ceil(currentHour / frequencyHours) * frequencyHours;
      return `Today at ${nextHour.toString().padStart(2, "0")}:00`;
    }

    for (let offset = 1; offset <= 7; offset++) {
      const candidate = new Date(now);
      candidate.setDate(now.getDate() + offset);
      const candidateDayId = DAY_IDS_BY_JS_DAY[candidate.getDay()];
      if (!selectedDays.includes(candidateDayId)) continue;

      if (offset === 1) {
        return `Tomorrow at ${startTime}`;
      }
      const fullName =
        DAYS_OF_WEEK.find((day) => day.id === candidateDayId)?.fullName ??
        candidateDayId;
      return `${fullName} at ${startTime}`;
    }

    return "No days selected";
  };

  const handleSave = () => {
    const schedule: SyncSchedule = {
      enabled: true,
      daysOfWeek: selectedDays,
      startTime,
      endTime,
      frequencyMinutes: parseInt(syncFrequency),
    };
    onSave(schedule);
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to original values
    if (dataSource.syncSchedule) {
      setSelectedDays(dataSource.syncSchedule.daysOfWeek);
      setStartTime(dataSource.syncSchedule.startTime);
      setEndTime(dataSource.syncSchedule.endTime);
      setSyncFrequency(dataSource.syncSchedule.frequencyMinutes.toString());
    }
    onOpenChange(false);
  };

  const handleDisableSchedule = () => {
    setShowDisableConfirm(true);
  };

  const confirmDisableSchedule = () => {
    const disabledSchedule: SyncSchedule = {
      enabled: false,
      daysOfWeek: [],
      startTime: "00:00",
      endTime: "00:00",
      frequencyMinutes: 0, // Manual only
    };
    onSave(disabledSchedule);
    setShowDisableConfirm(false);
    onOpenChange(false);
  };

  // Check if schedule is already disabled (manual only)
  const isScheduleActive =
    dataSource.syncSchedule?.enabled !== false &&
    dataSource.syncSchedule?.frequencyMinutes !== 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Sync Schedule - {dataSource.name}
          </DialogTitle>
          <DialogDescription>
            Configure automatic synchronization schedule for this data source
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Days of Week Selector */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Days of Week</Label>
            <div className="flex gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <Button
                  key={day.id}
                  variant={
                    selectedDays.includes(day.id) ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => toggleDay(day.id)}
                  className={`flex-1 ${
                    selectedDays.includes(day.id)
                      ? "bg-teal-600 hover:bg-teal-700 text-white"
                      : "hover:bg-muted"
                  }`}
                >
                  {day.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Time Window */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Sync Between</Label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="Start time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-muted-foreground">to</span>
              <div className="flex-1">
                <Select value={endTime} onValueChange={setEndTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="End time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Sync Frequency */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Sync Every</Label>
            <Select value={syncFrequency} onValueChange={setSyncFrequency}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                {SYNC_FREQUENCIES.map((freq) => (
                  <SelectItem key={freq.value} value={freq.value}>
                    {freq.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule Preview Section */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <Clock className="h-5 w-5 text-teal-600 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">Schedule Preview</p>
                <p className="text-sm text-muted-foreground">
                  Next sync:{" "}
                  <span className="font-semibold text-foreground">
                    {getNextSyncTime()}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Syncs per day:{" "}
                  <span className="font-semibold text-foreground">
                    ~{calculateSyncsPerDay()} (between {startTime}-{endTime})
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Info Panel */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900 dark:text-blue-100">
                Scheduled syncs run automatically in the background during the
                configured time window. You can still use 'Sync Now' for
                immediate manual refresh at any time.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDisableSchedule}
              disabled={!isScheduleActive}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20"
            >
              Disable Schedule
            </Button>
            <Button
              onClick={handleSave}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Schedule
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Disable Schedule Confirmation Dialog */}
      <Dialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Automated Sync?</DialogTitle>
            <DialogDescription>
              This will stop automated syncing for{" "}
              <strong>{dataSource.name}</strong>. You'll need to use 'Sync Now'
              manually.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisableConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="outline" onClick={confirmDisableSchedule}>
              Disable Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
